import { Link } from "@medoma/components/Link/Link";
import { getPatientNameWithStatus, type IListPatient } from "@/api/Patients";
import {
  getPatientFamilyName,
  getPatientGivenName,
  patientStatusDictionary,
  patientStatusSchema,
} from "@medoma/models/patients";
import { useLingui } from "@lingui/react/macro";
import { FlagsPopover } from "@/components/FlagsPopover/FlagsPopover";
import styles from "./NameCell.module.scss";

const NameLink = ({
  patient,
  nameType,
  showPatientStatus,
}: {
  patient: Exclude<
    IListPatient,
    { status: typeof patientStatusSchema.enum.deleted }
  >;
  nameType: "given" | "family";
  showPatientStatus: boolean;
}) => (
  <Link to={`/commandcenter/patients/${patient.id}`} weight="regular">
    {showPatientStatus
      ? getPatientNameWithStatus(patient, nameType)
      : nameType === "given"
        ? getPatientGivenName(patient.name)
        : getPatientFamilyName(patient.name)}
  </Link>
);

const NameCell = ({
  patient,
  nameType,
  showPatientStatus = false,
  showFlags = false,
}: {
  patient: IListPatient;
  nameType: "given" | "family";
  showPatientStatus?: boolean;
  showFlags?: boolean;
}) => {
  const { t } = useLingui();
  if (patient.status === patientStatusSchema.enum.deleted) {
    return t(patientStatusDictionary.deleted.singular);
  }
  const nameLink = (
    <NameLink
      patient={patient}
      nameType={nameType}
      showPatientStatus={showPatientStatus}
    />
  );
  if (showFlags && patient.flags) {
    return (
      <div className={styles.patientWithFlags}>
        {nameLink}
        <FlagsPopover details={patient.flags} />
      </div>
    );
  }
  return nameLink;
};

export const FamilyNameCell = ({
  patient,
  showPatientStatus = false,
}: StrictOmit<Parameters<typeof NameCell>[0], "nameType">) => {
  return (
    <NameCell
      patient={patient}
      nameType="family"
      showPatientStatus={showPatientStatus}
      showFlags={true}
    />
  );
};

export const GivenNameCell = ({
  patient,
  showPatientStatus = false,
}: StrictOmit<Parameters<typeof NameCell>[0], "nameType">) => {
  return (
    <NameCell
      patient={patient}
      nameType="given"
      showPatientStatus={showPatientStatus}
    />
  );
};
